import request from '@/middleware/request'

const getModels = (data = {}) => {
  const queryParams = new URLSearchParams()

  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined) {
      queryParams.append(key, encodeURIComponent(value))
    }
  }

  return request({
    url: `/ai/models?${queryParams.toString()}`,
    method: 'get'
  })
}

const getModelById = id => {
  return request({
    url: `/ai/models/${id}`,
    method: 'get'
  })
}

const createModel = data => {
  return request({
    url: '/ai/models',
    method: 'post',
    data
  })
}

const updateModel = (id, data) => {
  return request({
    url: `/ai/models/${id}`,
    method: 'patch',
    data
  })
}

const deleteModel = id => {
  return request({
    url: `/ai/models/${id}`,
    method: 'delete'
  })
}

const buildModel = id => {
  return request({
    url: `/ai/models/${id}/build`,
    method: 'post'
  })
}

const predictModel = (id, data) => {
  return request({
    url: `/ai/models/${id}/predict`,
    method: 'post',
    data
  })
}

const testModel = (model_id, testdata_id) => {
  return request({
    url: `/ai/models/${model_id}/test/${testdata_id}`,
    method: 'post'
  })
}

const exportModel = model_id => {
  return request({
    url: `/ai/models/${model_id}/export`,
    method: 'get',
    responseType: 'blob'
  })
}

const importModel = data => {
  return request({
    url: `/ai/models/import`,
    method: 'post',
    data
  })
}

const getEntities = id => {
  return request({
    url: `/ai/entities?model_id=${id}`,
    method: 'get'
  })
}

const createEntity = data => {
  return request({
    url: '/ai/entities',
    method: 'post',
    data
  })
}

const updateEntity = (id, data) => {
  return request({
    url: `/ai/entities/${id}`,
    method: 'patch',
    data
  })
}

const deleteEntity = id => {
  return request({
    url: `/ai/entities/${id}`,
    method: 'delete'
  })
}

const getIntents = id => {
  return request({
    url: `/ai/intents?model_id=${id}`,
    method: 'get'
  })
}

const getIntentsAndTrainingPhrase = id => {
  return request({
    url: `/ai/intents/training-phrase?model_id=${id}`,
    method: 'get'
  })
}

const createIntent = data => {
  return request({
    url: '/ai/intents',
    method: 'post',
    data
  })
}

const createBulkIntents = data => {
  return request({
    url: '/ai/bulk-intents',
    method: 'post',
    data
  })
}

const updateIntent = (id, data) => {
  return request({
    url: `/ai/intents/${id}`,
    method: 'patch',
    data
  })
}

const deleteIntent = (id, options) => {
  const queryParams = new URLSearchParams()
  queryParams.append('options', options)
  return request({
    url: `/ai/intents/${id}?${queryParams.toString()}`,
    method: 'delete'
  })
}

const priorityIntent = data => {
  return request({
    url: `/ai/intents/priority`,
    method: 'post',
    data
  })
}

const deleteAllIntent = modelId => {
  return request({
    url: `/ai/intents/all/${modelId}`,
    method: 'delete'
  })
}

const getTrainingPhrases = data => {
  const queryParams = new URLSearchParams()

  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined) {
      queryParams.append(key, encodeURIComponent(value))
    }
  }

  return request({
    url: `/ai/training-phrases?${queryParams.toString()}`,
    method: 'get'
  })
}

const createTrainingPhrase = data => {
  return request({
    url: `/ai/training-phrases`,
    method: 'post',
    data
  })
}

const updateMultipleTrainingPhrase = data => {
  return request({
    url: '/ai/training-phrases/update-multiple',
    method: 'patch',
    data
  })
}

const deleteMultipleTrainingPhrase = data => {
  return request({
    url: '/ai/training-phrases/delete-multiple',
    method: 'delete',
    data
  })
}

const uploadTrainingPhrase = data => {
  return request({
    url: '/ai/training-phrases/upload',
    method: 'post',
    data
  })
}

const exportTestingPhrase = data => {
  return request({
    url: '/ai/testing-phrases/export',
    method: 'post',
    data
  })
}

const exportTrainingPhrase = data => {
  return request({
    url: '/ai/training-phrases/export',
    method: 'post',
    data
  })
}

const getConsistencyGroup = id => {
  return request({
    url: `/ai/training-phrases/consistency-group?model_id=${id}`,
    method: 'get'
  })
}

const checkConsistencyTrainingPhrase = id => {
  return request({
    url: `/ai/training-phrases/check-consistency?model_id=${id}`,
    method: 'get'
  })
}

const clearConsistencyTrainingPhrase = (id, body) => {
  return request({
    url: `/ai/training-phrases/clear-consistency?model_id=${id}`,
    method: 'patch',
    data: body
  })
}

const findAndReplaceTrainingPhrase = (queryString, body) => {
  const queryParams = new URLSearchParams()

  for (const [key, value] of Object.entries(queryString)) {
    if (value !== null && value !== undefined) {
      queryParams.append(key, encodeURIComponent(value))
    }
  }
  return request({
    url: `/ai/training-phrases/find-and-replace?${queryParams.toString()}`,
    method: 'patch',
    data: body
  })
}

const assistantTagging = id => {
  return request({
    url: `/ai/labeling-assistant/${id}`,
    method: 'get'
  })
}

const clearAssistantTagging = id => {
  return request({
    url: `/ai/clear-labeling-assistant/${id}`,
    method: 'patch'
  })
}

const getTask = (id, task, options = false) => {
  let url = `/ai/task/${id}?task=${task}`
  if (options !== false) {
    const queryParams = new URLSearchParams()
    for (const [key, value] of Object.entries(options)) {
      if (value !== null && value !== undefined) {
        queryParams.append(key, encodeURIComponent(value))
      }
    }
    url = `${url}&${queryParams.toString()}`
  }
  return request({
    url,
    method: 'get'
  })
}

const getTestdata = (id, task) => {
  return request({
    url: `/ai/testdata?model_id=${id}`,
    method: 'get'
  })
}

const updateTestdata = (id, data) => {
  return request({
    url: `/ai/testdata/${id}`,
    method: 'patch',
    data
  })
}

const createTestdata = data => {
  return request({
    url: '/ai/testdata',
    method: 'post',
    data
  })
}

const deleteTestdata = (id, options) => {
  const queryParams = new URLSearchParams()
  queryParams.append('options', options)
  return request({
    url: `/ai/testdata/${id}?${queryParams.toString()}`,
    method: 'delete'
  })
}

const deleteMultipleTestingPhrase = data => {
  return request({
    url: '/ai/testing-phrases/delete-multiple',
    method: 'delete',
    data
  })
}

const getTestingPhrases = data => {
  const queryParams = new URLSearchParams()

  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined) {
      queryParams.append(key, encodeURIComponent(value))
    }
  }

  return request({
    url: `/ai/testing-phrases?${queryParams.toString()}`,
    method: 'get'
  })
}

const createTestingPhrase = data => {
  return request({
    url: '/ai/testing-phrases',
    method: 'post',
    data
  })
}

const updateMultipleTestingPhrase = data => {
  return request({
    url: '/ai/testing-phrases/update-multiple',
    method: 'patch',
    data
  })
}

const getModelLog = (id, data) => {
  const queryParams = new URLSearchParams()

  for (const [key, value] of Object.entries(data)) {
    if (value !== null && value !== undefined) {
      queryParams.append(key, encodeURIComponent(value))
    }
  }

  return request({
    url: `/ai/models/${id}/log?${queryParams.toString()}`,
    method: 'get'
  })
}

const updateModelLog = (id, data) => {
  return request({
    url: `/ai/models/${id}/log`,
    method: 'patch',
    data
  })
}

const getAiDefaultConfig = () => {
  return request({
    url: `/ai/default-config`,
    method: 'get'
  })
}

const reloadModel = id => {
  return request({
    url: `/ai/models/${id}/reload`,
    method: 'get'
  })
}

const unloadModel = id => {
  return request({
    url: `/ai/models/${id}/unload`,
    method: 'get'
  })
}

const clearBuild = id => {
  return request({
    url: `/ai/models/${id}/clear-build`,
    method: 'patch'
  })
}

export default {
  getModels,
  getModelById,
  createModel,
  updateModel,
  deleteModel,
  buildModel,
  predictModel,
  testModel,
  exportModel,
  importModel,

  getEntities,
  createEntity,
  updateEntity,
  deleteEntity,

  getIntents,
  getIntentsAndTrainingPhrase,
  createIntent,
  createBulkIntents,
  updateIntent,
  deleteIntent,
  priorityIntent,
  deleteAllIntent,

  getTrainingPhrases,
  createTrainingPhrase,
  updateMultipleTrainingPhrase,
  deleteMultipleTrainingPhrase,
  uploadTrainingPhrase,
  exportTestingPhrase,
  exportTrainingPhrase,

  getConsistencyGroup,
  checkConsistencyTrainingPhrase,
  clearConsistencyTrainingPhrase,
  findAndReplaceTrainingPhrase,

  assistantTagging,
  clearAssistantTagging,

  getTask,

  getTestdata,
  updateTestdata,
  createTestdata,
  deleteTestdata,

  getTestingPhrases,
  createTestingPhrase,
  updateMultipleTestingPhrase,
  deleteMultipleTestingPhrase,

  getModelLog,
  updateModelLog,
  getAiDefaultConfig,

  reloadModel,
  unloadModel,

  clearBuild
}
